import { IDonationRate } from "@/props/IDonation";
import { Button, Input, Form, message } from "antd";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { donateDreamPoints } from "@/api/donation";
import { DREAM_POINT, DREAM_VALUE_DONATION } from "@/constant/donation";
import DonateConfirmModal from "@/components/Modal/DonateConfirmModal";
import DonateSuccessModal from "../Modal/DonationSuccessModal";

// 梦想值捐赠
const DreamPointsDonation = ({
  dreamPointRate,
  isLogin,
  isMobile,
  hasEnded,
}: {
  dreamPointRate: IDonationRate;
  isLogin: boolean;
  isMobile: boolean;
  hasEnded: boolean;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const currentLocale: string | undefined = router.locale?.toUpperCase() || "EN";
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [inputValue, setInputValue] = useState<number>(0);
  const [isInvalid, setIsInvalid] = useState(false);
  const commonDP = [10, 60, 80, 660, 880, 1680];
  const DonationConfirmRef: any = useRef(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const labelTextStyle = isMobile ? "text-base" : "text-xl";
  const containerMarginTop = isMobile ? "mt-10" : "mt-[144px]";
  const inputWidth = isMobile ? "w-full text-xl" : "w-[440px]";
  const buttonWidth = isMobile ? "w-full text-base" : "w-[320px]";
  const luckyBoxWidth = isMobile ? "w-[74px]" : "w-[214px]";

  // 处理预设值点击
  const handlePresetClick = (value: number, index: number) => {
    setActiveIndex(index);
    setInputValue(value);
  };

  // 处理输入框变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    let value = rawValue === "" ? 0 : parseInt(rawValue) || 0;
    if (value < 0) {
      value = 0;
    }
    setInputValue(value);
    setActiveIndex(null);
    setIsInvalid(value !== 0 && value % 10 !== 0);
  };

  useEffect(() => {
    const checkHash = () => {
      if (window.location.hash === "#donate-now") {
        const element = document.getElementById("donate-now");
        if (element) {
          requestAnimationFrame(() => {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
            window.scrollBy(0, -100);
          });
        }
      }
    };
    checkHash();
    window.addEventListener("hashchange", checkHash);
    return () => window.removeEventListener("hashchange", checkHash);
  }, []);

  const handleClickDonate = async () => {
    if (hasEnded) return;
    if (!isLogin) {
      const currentPath = router.asPath.split("#")[0];
      router.push({ pathname: "/login", query: { redirect: `${currentPath}#donate-now` } });
      return;
    }
    if (isInvalid) return;
    if (!inputValue) {
      message.warning(t("csr.please_input_dream_value"));
      return;
    }
    DonationConfirmRef.current.setOpen(true);
  };

  const handleDonate = async () => {
    try {
      const res: any = await donateDreamPoints({
        eventId: 1,
        type: DREAM_VALUE_DONATION,
        unitType: DREAM_POINT,
        donatedDreamPoints: inputValue,
      });
      if (res === undefined) return;
      DonationConfirmRef.current.setOpen(false);
      setSuccessModalOpen(true);
    } catch (errorInfo) {
      console.log("Failed:", errorInfo);
    }
  };

  return (
    <div
      className={`flex max-w-[1440px] ${containerMarginTop} ${isMobile ? "mx-4" : "mx-auto scroll-mt-[86px]"}`}
      id="donate-now"
    >
      {!isMobile && (
        <div className="mr-20">
          <Image
            src={currentLocale === "TC" ? "/images/csr/csr_donate_tc.webp" : "/images/csr/csr_donate.webp"}
            alt="csr_small"
            className="rounded-[32px]"
            width={456}
            height={812}
          />
        </div>
      )}
      <div className={`flex flex-1 flex-col ${isMobile ? "" : "justify-between"}`}>
        <div>
          <h3
            className={`mb-4 font-semibold text-stone-800 ${
              isMobile ? "h-[48px] text-center text-2xl" : "h-[60px] text-5xl"
            }`}
          >
            {t("common.dream_value")}
            <span className="ml-1 inline-block bg-yellow-600 p-2">{t("csr.donation")}</span>
          </h3>
          <p className={`text-[#4c4c4c] ${isMobile ? "text-sm" : "text-xl"}`}>{t("csr.donation_desc")}</p>
        </div>
        <div>
          <p className={`mb-3 font-semibold text-[#4c4c4c] ${isMobile ? "mt-10 text-base" : "text-xl"}`}>
            {t("csr.lucky_donation")}：
          </p>
          <div className={`${isMobile ? "mb-10 grid grid-cols-4 gap-3" : "flex flex-wrap gap-x-4 gap-y-3"}`}>
            {commonDP.map((value, index) => (
              <div
                key={index}
                onClick={() => handlePresetClick(value, index)}
                data-qt="csr_donation_preset_click"
                data-qt-preset-value={value}
                className={`flex h-[56px] cursor-pointer items-center justify-center rounded-xl border text-lg font-medium transition-colors
                  ${activeIndex === index ? "border-[#d91d22]" : "border-[#c9cfd8]"} 
                  ${isMobile ? "w-full" : luckyBoxWidth}`}
              >
                {value}
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className={`mb-3 font-semibold text-[#4c4c4c] ${labelTextStyle}`}>{t("csr.dream_donation")}：</p>
          <Form.Item
            validateStatus={isInvalid ? "error" : ""}
            help={isInvalid ? t("csr.please_input_multiple_of_10") : ""}
            className="!mb-0"
          >
            <Input
              type="text"
              placeholder={t("csr.please_input_dream_value")}
              className={`h-[56px] rounded-xl text-center ${inputWidth}`}
              value={inputValue === 0 ? "" : inputValue.toString()}
              onChange={handleInputChange}
              onKeyPress={(e) => {
                // 阻止输入负号
                if (e.key === "-") {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>
        </div>
        <div className={`${isMobile ? "my-10" : ""}`}>
          <p className={`text-stone-800 ${isMobile ? "mb-2 text-sm" : "mb-4 text-base"}`}>
            {dreamPointRate?.unitValue}
            {t("common.dream_value")} = {dreamPointRate?.rate} USD
          </p>
          <p className={`text-stone-800 ${isMobile ? "text-base" : "text-2xl"}`}>
            {t("csr.expected_donation")}： {inputValue === 0 ? "-" : (inputValue / 1000).toFixed(2)} USD
          </p>
        </div>
        <Button
          type="primary"
          size="large"
          className={`h-[56px] ${buttonWidth}`}
          onClick={handleClickDonate}
          disabled={hasEnded}
          data-qt="csr_donate_now_click"
          data-qt-donation-value={inputValue}
        >
          {t("csr.donate_now")}
        </Button>
      </div>
      <DonateConfirmModal onRef={DonationConfirmRef} onConfirm={handleDonate} inputValue={inputValue} />
      <DonateSuccessModal
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        onContinue={() => setSuccessModalOpen(false)}
        onViewRecord={() => router.push("/user/points?tab=donation")}
      />
    </div>
  );
};

export default DreamPointsDonation;
